import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Box, Typography, Select, MenuItem, InputBase, useTheme } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { getDynamicFormStore } from './useDynamicFormStore';
import DynamicFormFieldDesigner from '../pages/forms/DynamicFormFieldDesigner';
import { useFormPalette } from '../pages/forms/DynamicFormPrimitives';
import { NODE_UI } from '../NodeUI/nodeUiStyles';
import PublicAccessPolicyEditor from '../access/PublicAccessPolicyEditor';
import useTaskInboxStore from '../pages/useTaskInboxStore';

const RESPONSE_MODE_DESCRIPTIONS = {
  FILL_SUBMIT: 'Users fill out the form and submit. No drafts are saved on the server. Each visit starts with a blank form.',
  FILL_REEDIT: 'Users can save drafts and return via a unique URL to continue editing. Drafts are stored on the server and automatically expire after 30 days.',
  VIEW_ONLY: 'Users can view the form but cannot edit or submit responses.',
};

function SectionSelect({ label, value, onChange, options, theme, palette }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Typography
        component="label"
        sx={{
          fontSize: NODE_UI.fontSize.label,
          fontWeight: NODE_UI.fontWeight.label,
          color: palette.textSecondary,
          lineHeight: 1.4,
          userSelect: 'none',
        }}
      >
        {label}
      </Typography>
      <Select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        sx={{
          fontFamily: 'inherit',
          fontSize: NODE_UI.fontSize.value,
          color: palette.textPrimary,
          backgroundColor: theme.palette.background.default,
          borderRadius: NODE_UI.borderRadiusSm,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.textSecondary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.textPrimary,
          },
          '& .MuiSelect-select': {
            padding: NODE_UI.input.padding,
          },
        }}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </Select>
    </Box>
  );
}

function SettingsSection({ title, children, palette }) {
  return (
    <Box
      sx={{
        border: `1px solid ${palette.border}`,
        borderRadius: NODE_UI.borderRadius,
        backgroundColor: palette.surface,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: '12px',
          py: '7px',
          backgroundColor: palette.surfaceSubtle,
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <Typography
          sx={{
            fontSize: NODE_UI.fontSize.value,
            fontWeight: 600,
            color: palette.textPrimary,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: '12px', py: '10px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {children}
      </Box>
    </Box>
  );
}

function CoverImageSetting({ value, onChange, palette }) {
  const [previewError, setPreviewError] = useState(false);

  const handleChange = useCallback((e) => {
    setPreviewError(false);
    onChange(e.target.value);
  }, [onChange]);

  const handleClear = useCallback(() => {
    setPreviewError(false);
    onChange('');
  }, [onChange]);

  const showPreview = value && !previewError;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <Typography
        sx={{
          fontSize: NODE_UI.fontSize.label,
          fontWeight: NODE_UI.fontWeight.label,
          color: palette.textSecondary,
          lineHeight: 1.4,
          userSelect: 'none',
        }}
      >
        Cover image URL
      </Typography>
      <Box sx={{ position: 'relative' }}>
        <InputBase
          value={value || ''}
          onChange={handleChange}
          placeholder="https://example.com/image.jpg"
          fullWidth
          sx={{
            fontFamily: 'inherit',
            fontSize: NODE_UI.fontSize.value,
            color: palette.textPrimary,
            border: `1px solid ${palette.border}`,
            borderRadius: NODE_UI.borderRadiusSm,
            px: '8px',
            py: '5px',
            transition: 'border-color 0.15s ease',
            '&:hover': { borderColor: palette.textSecondary },
            '&.Mui-focused': { borderColor: palette.textPrimary },
            '& input::placeholder': { color: palette.textSecondary, opacity: 0.5 },
          }}
        />
        {value && (
          <Box
            onClick={handleClear}
            sx={{
              position: 'absolute',
              right: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: palette.chipBg,
              '&:hover': { backgroundColor: palette.border },
            }}
          >
            <CloseIcon sx={{ fontSize: 12, color: palette.textSecondary }} />
          </Box>
        )}
      </Box>
      {showPreview ? (
        <Box
          sx={{
            width: '100%',
            height: 80,
            borderRadius: '6px',
            overflow: 'hidden',
            border: `1px solid ${palette.border}`,
          }}
        >
          <Box
            component="img"
            src={value}
            alt="Cover preview"
            onError={() => setPreviewError(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            height: 48,
            borderRadius: '6px',
            border: `1px dashed ${palette.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <ImageOutlinedIcon sx={{ fontSize: 16, color: palette.textSecondary, opacity: 0.4 }} />
          <Typography sx={{ fontSize: 11, color: palette.textSecondary, opacity: 0.4 }}>
            {previewError ? 'Could not load image' : 'No cover image set'}
          </Typography>
        </Box>
      )}
      <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: palette.textSecondary, opacity: 0.5 }}>
        Displayed at the top of the public form page. Leave empty for default.
      </Typography>
    </Box>
  );
}

function TabBar({ tabs, active, onChange, palette }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '2px',
        p: '3px',
        backgroundColor: palette.surfaceSubtle,
        borderRadius: '8px',
        border: `1px solid ${palette.border}`,
        mb: '12px',
      }}
    >
      {tabs.map((tab, i) => (
        <Box
          key={tab}
          onClick={() => onChange(i)}
          sx={{
            flex: 1,
            textAlign: 'center',
            py: '5px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: NODE_UI.fontSize.value,
            fontWeight: active === i ? 600 : 400,
            color: active === i ? palette.textPrimary : palette.textSecondary,
            backgroundColor: active === i ? palette.surface : 'transparent',
            boxShadow: active === i ? NODE_UI.shadow : 'none',
            transition: 'all 0.15s ease',
            userSelect: 'none',
          }}
        >
          {tab}
        </Box>
      ))}
    </Box>
  );
}

export default function DynamicFormBuilderTemplate({
  component_id,
  projectId,
  setChildTemplateLoaded,
  availablePlaceholders,
}) {
  const store = useMemo(() => getDynamicFormStore(component_id), [component_id]);
  const data = store((s) => s.data);
  const loaded = store((s) => s.loaded);
  const palette = useFormPalette();
  const theme = useTheme();

  const [tab, setTab] = React.useState(0);

  const activeAccountUsers = useTaskInboxStore((s) => s.activeAccountUsers);
  const activeAccountUsersLoading = useTaskInboxStore((s) => s.activeAccountUsersLoading);
  const fetchActiveAccountUsers = useTaskInboxStore((s) => s.fetchActiveAccountUsers);

  useEffect(() => {
    if (data.accessPolicy?.mode === 'AUTHENTICATED_SELECTED_USERS') {
      fetchActiveAccountUsers();
    }
  }, [data.accessPolicy?.mode, fetchActiveAccountUsers]);

  useEffect(() => {
    if (loaded && setChildTemplateLoaded) setChildTemplateLoaded(true);
  }, [loaded, setChildTemplateLoaded]);

  const handleFieldsChange = useCallback(
    (fields) => store.getState().setFields(fields),
    [store]
  );
  const handleTitleChange = useCallback(
    (val) => store.getState().setTitle(val),
    [store]
  );
  const handleDescriptionChange = useCallback(
    (val) => store.getState().setDescription(val),
    [store]
  );
  const handleAccessPolicyChange = useCallback(
    (val) => store.getState().setAccessPolicy(val),
    [store]
  );
  const handleResponseModeChange = useCallback(
    (val) => store.getState().setResponseMode(val),
    [store]
  );
  const handleCoverImageChange = useCallback(
    (val) => store.getState().setFormSettings({ coverImageUrl: val }),
    [store]
  );

  if (!loaded) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <TabBar
        tabs={['Form Design', 'Settings']}
        active={tab}
        onChange={setTab}
        palette={palette}
      />

      {tab === 0 && (
        <>
          <DynamicFormFieldDesigner
            fields={data.fields}
            onChange={handleFieldsChange}
            title={data.title}
            description={data.description}
            onTitleChange={handleTitleChange}
            onDescriptionChange={handleDescriptionChange}
            availablePlaceholders={availablePlaceholders}
          />
        </>
      )}

      {tab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <SettingsSection title="General" palette={palette}>
            <PublicAccessPolicyEditor
              accessPolicy={data.accessPolicy}
              onChange={handleAccessPolicyChange}
              users={activeAccountUsers}
              usersLoading={activeAccountUsersLoading}
            />
            <SectionSelect
              label="Response Mode"
              value={data.responseMode}
              onChange={handleResponseModeChange}
              options={[
                { value: 'FILL_SUBMIT', label: 'Submit only' },
                { value: 'FILL_REEDIT', label: 'Save & resume drafts' },
                { value: 'VIEW_ONLY', label: 'View only' },
              ]}
              theme={theme}
              palette={palette}
            />
            <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: palette.textSecondary, opacity: 0.75 }}>
              {RESPONSE_MODE_DESCRIPTIONS[data.responseMode] || ''}
            </Typography>
          </SettingsSection>
          <SettingsSection title="Appearance" palette={palette}>
            <CoverImageSetting
              value={data.formSettings?.coverImageUrl || ''}
              onChange={handleCoverImageChange}
              palette={palette}
            />
          </SettingsSection>
        </Box>
      )}
    </Box>
  );
}
